import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { settingsService } from 'v2/shared/services/settings-service'
import { getAPIConsoleQueryKeys } from 'v2/shared/utils/query-key-management'
import type { RPlivoApplication } from 'v2/types/api/settings'
import { errorToast } from 'v2/utils/toasts'
import type {
  CreateApplicationFormValues,
  CreateTrunkFormValues,
  DeleteApplicationPayload,
  DeleteTrunkPayload,
  RTrunk,
  UpdateApplicationPayload,
  UpdateTrunkPayload,
} from './types'

// Query to get single application details
export const useApplicationDetailQuery = (appId?: string) =>
  useQuery({
    queryKey: [...getAPIConsoleQueryKeys().xmlApplications, 'detail', appId],
    queryFn: async () => {
      if (!appId) return null
      const response = await settingsService.getApplication(appId)
      return response.data as RPlivoApplication
    },
    enabled: !!appId,
  })

// Application mutations
export const useCreateApplicationMutation = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (payload: CreateApplicationFormValues) => {
      const response = await settingsService.createApplication(
        payload as unknown as Record<string, unknown>,
      )
      return response.data as RPlivoApplication
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: getAPIConsoleQueryKeys().xmlApplications })
    },
  })
}

export const useUpdateApplicationMutation = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (payload: UpdateApplicationPayload) => {
      const response = await settingsService.updateApplication(payload.app_id, payload.payload)
      return response.data as { message: string; api_id: string }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: getAPIConsoleQueryKeys().xmlApplications })
    },
  })
}

export const useDeleteApplicationMutation = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (payload: DeleteApplicationPayload) => {
      await settingsService.deleteApplication(payload.app_id, {
        cascade: payload.cascade,
        new_endpoint_application: payload.new_endpoint_application,
      })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: getAPIConsoleQueryKeys().xmlApplications })
    },
  })
}

export const useUnlinkNumberFromApplicationMutation = (applicationId?: number | string) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (phoneNumber: string) => {
      await settingsService.unlinkNumberFromApplication(phoneNumber)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: getAPIConsoleQueryKeys().xmlApplications })
      if (applicationId != null) {
        queryClient.invalidateQueries({
          queryKey: [...getAPIConsoleQueryKeys().xmlApplications, 'numbers', String(applicationId)],
        })
      }
    },
  })
}

export const useUnlinkNumberFromTrunkMutation = (trunkId?: number | string) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (phoneNumber: string) => {
      await settingsService.unlinkInboundTrunkNumber(phoneNumber)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: getAPIConsoleQueryKeys().inboundTrunks })
      if (trunkId != null) {
        queryClient.invalidateQueries({
          queryKey: [...getAPIConsoleQueryKeys().inboundTrunks, 'numbers', String(trunkId)],
        })
      }
    },
  })
}

// Trunk
export const useTrunkDetailQuery = (trunkId?: string) =>
  useQuery({
    queryKey: [...getAPIConsoleQueryKeys().trunks, 'detail', trunkId],
    queryFn: async () => {
      if (!trunkId) return null
      const response = await settingsService.getTrunk(trunkId)
      return (response.data as { object: RTrunk }).object as RTrunk
    },
    enabled: !!trunkId,
  })

export const useCreateTrunkMutation = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (payload: CreateTrunkFormValues) => {
      const response = await settingsService.createTrunk(
        payload as unknown as Record<string, unknown>,
      )
      return response.data as RTrunk
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: getAPIConsoleQueryKeys().trunks })
    },
  })
}

export const useUpdateTrunkMutation = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (payload: UpdateTrunkPayload) => {
      const response = await settingsService.updateTrunk(payload.trunk_id, payload.payload)
      return response.data as { message: string; api_id: string }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: getAPIConsoleQueryKeys().trunks })
    },
  })
}

export const useDeleteTrunkMutation = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (payload: DeleteTrunkPayload) => {
      await settingsService.deleteTrunk(payload.trunk_id)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: getAPIConsoleQueryKeys().trunks })
    },
  })
}

// Origination URI mutations
export const useCreateOriginationURIMutation = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (payload: {
      name: string
      uri: string
      sip_user?: string
      username?: string
      password?: string
    }) => {
      return settingsService.createZentrunkURI(payload)
    },
    // originationURIsBase, not zentrunkUris: also covers per-uuid detail keys.
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: getAPIConsoleQueryKeys().originationURIsBase })
    },
  })
}

export const useUpdateOriginationURIMutation = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      uriId,
      ...payload
    }: {
      uriId: string | number
    } & {
      name: string
      uri: string
      sip_user?: string
      username?: string
      password?: string
    }) => {
      return settingsService.updateZentrunkURI(uriId, payload)
    },
    // Also invalidates the per-uuid detail key, or a reopened Edit could
    // serve a stale sip_user from before this save.
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: getAPIConsoleQueryKeys().originationURIsBase })
    },
  })
}

export const useDeleteOriginationURIMutation = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (uriId: string | number) => {
      return settingsService.deleteZentrunkURI(uriId)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: getAPIConsoleQueryKeys().originationURIsBase })
    },
  })
}

// Credential mutations
export const useCreateCredentialMutation = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (payload: { name: string; username: string; password: string }) => {
      return settingsService.createCredential(payload)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: getAPIConsoleQueryKeys().credentials })
    },
  })
}

export const useUpdateCredentialMutation = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      credential_uuid,
      ...payload
    }: {
      credential_uuid: string
    } & Partial<{ name: string; username: string; password: string }>) => {
      return settingsService.updateCredential(credential_uuid, payload)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: getAPIConsoleQueryKeys().credentials })
    },
  })
}

export const useDeleteCredentialMutation = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (credential_uuid: string) => {
      return settingsService.deleteCredential(credential_uuid)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: getAPIConsoleQueryKeys().credentials })
    },
  })
}

// IP Access Control List queries
export const useIPAccessControlListsQuery = (enabled: boolean = true) =>
  useQuery({
    queryKey: getAPIConsoleQueryKeys().ipAccessControlLists,
    queryFn: () => settingsService.getIPAccessControlLists(),
    enabled,
    staleTime: 0,
  })

// Credentials queries
export const useCredentialsQuery = (enabled: boolean = true) =>
  useQuery({
    queryKey: getAPIConsoleQueryKeys().credentials,
    queryFn: () => settingsService.getCredentials(),
    enabled,
    staleTime: 0,
  })

// Origination URIs queries
export const useOriginationURIsQuery = (enabled: boolean = true) =>
  useQuery({
    queryKey: getAPIConsoleQueryKeys().zentrunkUris,
    queryFn: () => settingsService.getZentrunkURIs(),
    enabled,
    staleTime: 0,
  })

/** Single-URI fetch for sip_user, which the LIST endpoint omits (SER-6495).
 * Shared factory: Edit modal and the URI dropdowns both use it. */
export const getOriginationURIQueryOptions = (uriUuid: string) => ({
  queryKey: getAPIConsoleQueryKeys().originationURI(uriUuid),
  queryFn: () => settingsService.getOriginationURI(uriUuid),
})

export const useOriginationURIQuery = (uriUuid: string | undefined, enabled: boolean) =>
  useQuery({
    ...getOriginationURIQueryOptions(uriUuid ?? ''),
    enabled: enabled && uriUuid !== undefined,
    staleTime: 0,
  })

/**
 * Which SIP platform each of the org's trunks was set up for. The platform is not
 * on the Plivo trunk record (no field for it), so core keeps it, and the edit
 * sheet needs it: LiveKit outbound cannot use an IP access control list, and that
 * rule has to hold past creation.
 *
 * One request for the whole org map, not one per trunk.
 */
export const useSipTrunkPlatformsQuery = (enabled: boolean = true) =>
  useQuery({
    queryKey: getAPIConsoleQueryKeys().sipTrunkPlatforms,
    queryFn: () => settingsService.getSipTrunkPlatforms(),
    enabled,
    staleTime: 0,
  })

/**
 * Records a trunk's platform after a successful create or update.
 *
 * Retried once, then reported. This write is not decorative metadata: for an
 * outbound LiveKit trunk it is the ONLY durable thing separating it from a
 * credentials-only custom trunk, so losing it silently recreates the bug this
 * whole change exists to fix (the next edit resolves to custom and offers an IP
 * ACL that LiveKit cannot use).
 *
 * The message deliberately leads with the trunk being saved, because it was: the
 * trunk call had already succeeded before this ran, and an error that reads as
 * "the trunk failed" would send the customer looking for a problem that is not
 * there. Re-saving the trunk re-records it, which is the recovery path.
 */
export const useRecordSipTrunkPlatformMutation = () => {
  const queryClient = useQueryClient()

  return useMutation({
    retry: 1,
    mutationFn: (vars: {
      trunkRef: string
      platform: string
      trunkDirection: 'inbound' | 'outbound'
    }) =>
      settingsService.recordSipTrunkPlatform(vars.trunkRef, {
        platform: vars.platform,
        trunk_direction: vars.trunkDirection,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: getAPIConsoleQueryKeys().sipTrunkPlatformsBase,
      })
    },
    onError: () => {
      errorToast({
        title: 'SIP platform not saved',
        description:
          'The trunk was saved, but we could not record which SIP platform it uses. Open the trunk and save it again to store it.',
      })
    },
  })
}

/** Drops the record when a trunk is deleted. Same silent-failure reasoning. */
export const useForgetSipTrunkPlatformMutation = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (trunkRef: string) => settingsService.forgetSipTrunkPlatform(trunkRef),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: getAPIConsoleQueryKeys().sipTrunkPlatformsBase,
      })
    },
  })
}

// IP Access Control List mutations
export const useCreateIPACLMutation = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (payload: { name: string; ip_addresses: string[] }) => {
      return settingsService.createIPAccessControlList(payload)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: getAPIConsoleQueryKeys().ipAccessControlLists })
    },
  })
}

export const useUpdateIPACLMutation = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      ipacl_uuid,
      ...payload
    }: {
      ipacl_uuid: string
    } & { name: string; ip_addresses: string[] }) => {
      return settingsService.updateIPAccessControlList(ipacl_uuid, payload)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: getAPIConsoleQueryKeys().ipAccessControlLists })
    },
  })
}

export const useDeleteIPACLMutation = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (ipacl_uuid: string) => {
      return settingsService.deleteIPAccessControlList(ipacl_uuid)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: getAPIConsoleQueryKeys().ipAccessControlLists })
    },
  })
}
